import { useEffect, useState } from "react";
import { Users, ShieldCheck, History, Database, Clock, Activity } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { MyRanksPanel } from "@/components/MyRanksPanel";
import { UrgentRequirementsTile } from "@/components/UrgentRequirementsTile";
import { GettingStartedPanel } from "@/components/GettingStarted";
import { PipelineAlertsPanel, OverridesTile } from "@/components/PipelineAlerts";

function Stat({ icon: Icon, label, value, hint, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Manrope" }}>{value ?? "—"}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent || "bg-slate-100 text-slate-700"}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState({ users: null, roles: null, audit24h: null, master: null });
  const [recentLogins, setRecentLogins] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const promises = [];
        if (hasPermission("users.view")) promises.push(api.get("/users")); else promises.push(Promise.resolve({ data: null }));
        if (hasPermission("roles.view")) promises.push(api.get("/roles")); else promises.push(Promise.resolve({ data: null }));
        if (hasPermission("audit.view")) promises.push(api.get("/audit-logs?limit=50")); else promises.push(Promise.resolve({ data: null }));
        if (hasPermission("master_data.view")) promises.push(api.get("/master-data")); else promises.push(Promise.resolve({ data: null }));
        const [u, r, a, m] = await Promise.all(promises);
        setStats({
          users: Array.isArray(u.data) ? u.data.length : null,
          roles: Array.isArray(r.data) ? r.data.length : null,
          audit24h: Array.isArray(a.data) ? a.data.length : null,
          master: Array.isArray(m.data) ? m.data.length : null,
        });
        if (Array.isArray(a.data)) setRecentLogins(a.data.slice(0, 6));
      } catch (_) {}
    })();
  }, [hasPermission]);

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Welcome back</div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>
          {user?.first_name} {user?.last_name}
        </h1>
        <div className="text-sm text-slate-500 mt-1">
          You're signed in as <span className="font-medium text-slate-700">{user?.role?.role_name || "No role"}</span>.
        </div>
      </div>

      {["users.view", "roles.view", "audit.view", "master_data.view"].some(hasPermission) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats">
          {hasPermission("users.view") && <Stat icon={Users} label="Active staff" value={stats.users} hint="All users in system" accent="bg-cyan-50 text-cyan-700" />}
          {hasPermission("roles.view") && <Stat icon={ShieldCheck} label="Custom roles" value={stats.roles} hint="Including Super Admin" accent="bg-indigo-50 text-indigo-700" />}
          {hasPermission("audit.view") && <Stat icon={History} label="Recent logins" value={stats.audit24h} hint="Latest events" accent="bg-emerald-50 text-emerald-700" />}
          {hasPermission("master_data.view") && <Stat icon={Database} label="Master records" value={stats.master} hint="Principals / vessels / ranks" accent="bg-amber-50 text-amber-700" />}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {user?.role?.role_name === "Super Admin" && <GettingStartedPanel selfId={user.id} />}
        {user?.role?.domain === "Sourcing" && <UrgentRequirementsTile />}
        {user?.role?.domain === "Sourcing" && <MyRanksPanel />}
        {["Super Admin", "Technical Superintendent/Assessment", "Management/HOD"].includes(user?.role?.role_name) && <PipelineAlertsPanel />}
        {["Super Admin", "Management/HOD"].includes(user?.role?.role_name) && <OverridesTile />}
        {hasPermission("audit.view") && (
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-slate-500" />
                <div className="text-sm font-semibold text-slate-800">Recent login activity</div>
              </div>
              <div className="text-xs text-slate-400">Audit-grade</div>
            </div>
            <table className="w-full crew-table">
              <thead>
                <tr>
                  <th className="text-left">User</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">IP</th>
                  <th className="text-left">Device</th>
                  <th className="text-left">Time</th>
                </tr>
              </thead>
              <tbody>
                {recentLogins.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-slate-400 py-8">No activity yet</td></tr>
                )}
                {recentLogins.map((l) => (
                  <tr key={l.id}>
                    <td className="font-medium text-slate-800">{l.user_name || l.email_attempted}</td>
                    <td>
                      <span className={`chip ${l.status === "success" ? "chip-success" : l.status === "failed" ? "chip-danger" : "chip-info"}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="font-mono text-xs text-slate-600">{l.ip_address || "—"}</td>
                    <td className="text-slate-600">{l.device || "—"}</td>
                    <td className="text-slate-500 text-xs"><Clock className="w-3 h-3 inline mr-1" />{new Date(l.login_time).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-sm font-semibold text-slate-800 mb-3">Your permissions</div>
          <div className="flex flex-wrap gap-1.5">
            {(user?.permissions || []).map((p) => (
              <span key={p} className="chip chip-info font-mono text-[11px]">{p}</span>
            ))}
            {(!user?.permissions || user.permissions.length === 0) && (
              <div className="text-xs text-slate-400">No permissions granted</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
